/**
 * ComingSoonPage — página de expectativa (modo lanzamiento).
 *
 * Vende con DESEO + data real de la base: reel intercalado de portadas de
 * cursos, caras de gurus (con foto) y eventos; contador regresivo; gancho
 * Founder (dcto de por vida para los primeros); captura de email (waitlist
 * con anti-spam, luego conectable a Resend para la campaña).
 */
import { useEffect, useMemo, useState } from 'react';
import { getCountryName } from '@/lib/constants/countries';
import { Logo } from '@/components/ui/logo';
import { useGurus } from '@/lib/api/gurus';
import { useReelCourses } from '@/lib/api/courses';
import { useUpcomingEvents } from '@/lib/api/events';
import { useJoinWaitlist } from '@/lib/api/waitlist';

// ── Lanzamiento ──────────────────────────────────────────────────────
// Corrido 2 semanas (antes lunes 10). Lunes 24 de agosto a las 9:00
// (GMT-5, hora Colombia/Perú = mismo instante que 9:00 Chicago CDT).
const LAUNCH_DATE = new Date('2026-08-24T09:00:00-05:00');


function useCountdown(target: Date) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, target.getTime() - now);
  return {
    d: Math.floor(diff / 86_400_000),
    h: Math.floor((diff % 86_400_000) / 3_600_000),
    m: Math.floor((diff % 3_600_000) / 60_000),
    s: Math.floor((diff % 60_000) / 1000),
  };
}

interface ReelItem { key: string; img: string; label: string; sub?: string; kind: 'guru' | 'curso' | 'evento'; }

export function ComingSoonPage() {
  const { data: gurus } = useGurus({});
  const { data: courses } = useReelCourses();
  const { data: events } = useUpcomingEvents();
  const join = useJoinWaitlist();
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(false);
  const cd = useCountdown(LAUNCH_DATE);

  // Reel INTERCALADO (guru → curso → evento → …), solo con imagen real.
  const reel = useMemo<ReelItem[]>(() => {
    const guruItems: ReelItem[] = (gurus ?? [])
      .filter((g) => g.profile?.avatar_url)
      .map((g) => ({
        key: `g-${g.user_id}`,
        img: g.profile!.avatar_url!,
        label: g.profile?.full_name ?? '',
        sub: g.profile?.country_code ? getCountryName(g.profile.country_code) : undefined,
        kind: 'guru',
      }));

    // Gurus en orden ALEATORIO por visita (Fisher–Yates). El directorio viene
    // ordenado por avg_rating desc, pero pre-lanzamiento nadie tiene ratings:
    // sin esto, los mismos ~6 de siempre acaparan los cupos del reel y los
    // gurus nuevos jamás aparecen. Cursos y eventos NO se tocan: su orden es
    // curado (destacados / fecha).
    for (let j = guruItems.length - 1; j > 0; j--) {
      const r = Math.floor(Math.random() * (j + 1));
      const tmp = guruItems[j]!;
      guruItems[j] = guruItems[r]!;
      guruItems[r] = tmp;
    }
    const courseItems: ReelItem[] = (courses ?? [])
      .filter((c) => c.cover_image_url)
      .map((c) => ({ key: `c-${c.id}`, img: c.cover_image_url!, label: c.title, kind: 'curso' }));
    const eventItems: ReelItem[] = (events ?? [])
      .filter((e) => e.cover_image_url)
      .map((e) => ({ key: `e-${e.id}`, img: e.cover_image_url!, label: e.title, kind: 'evento' }));

    // Round-robin para intercalar tipos (gurus salpicados entre cursos).
    const lists = [guruItems, courseItems, eventItems];
    const out: ReelItem[] = [];
    let i = 0;
    // Tope 24 (antes 16): con los tres tipos llenos salen 8 gurus, 8 cursos
    // y 8 eventos; el carrusel dura más pero cabe más elenco.
    while (out.length < 24 && lists.some((l) => l.length > 0)) {
      const item = lists[i % 3]?.shift();
      if (item) out.push(item);
      i++;
    }
    return out.length > 0 ? [...out, ...out] : [];
  }, [gurus, courses, events]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    try {
      await join.mutateAsync(email);
      setDone(true);
      setEmail('');
    } catch { /* error abajo */ }
  }

  const kindTag = { guru: 'Guru', curso: 'Curso', evento: 'Evento' } as const;
  const kindColor = {
    curso: 'bg-brand-accent text-[#071114]',
    guru: 'bg-brand-energy text-[#071114]',
    evento: 'bg-brand-mint text-[#071114]',
  } as const;

  return (
    <div className="min-h-screen bg-[#0a1a1f] text-white">
      <style>{`@keyframes gurumba-marquee { from { transform: translateX(0) } to { transform: translateX(-50%) } }`}</style>

      <header className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-6 py-4">
        <Logo size={48} dark className="shrink-0" />
        <span className="max-w-[10rem] text-right text-[11px] uppercase leading-relaxed tracking-[0.25em] text-white/35 sm:max-w-none">
          Lanzamiento en camino
        </span>
      </header>

      {/* Hero — editorial, sin cajas, con glow detrás del titular */}
      <section className="relative mx-auto max-w-4xl px-6 pt-2 text-center">
        {/* Glow con radial-gradient (no `filter: blur`): iOS Safari rompe el
            compositor con blurs grandes y deja la pantalla en blanco. */}
        <div
          aria-hidden
          className="pointer-events-none absolute left-1/2 top-4 -z-10 h-64 w-[38rem] max-w-full -translate-x-1/2"
          style={{ background: 'radial-gradient(ellipse at center, rgba(42,163,185,0.22), transparent 70%)' }}
        />

        <h1 className="font-display text-[2.5rem] font-bold leading-[1.03] [text-wrap:balance] md:text-6xl">
          Aprende de quien <span className="text-brand-accent">ya lo logró.</span>
          <br /> En vivo, uno a uno.
        </h1>

        <p className="mx-auto mt-4 max-w-md text-base text-white/55 md:text-lg">
          Los mejores gurus de Latinoamérica, a un clic. Sesiones 1:1, cursos, rutas y eventos.
        </p>

        {/* Founder — badge sólido: es el gancho de la campaña, tiene que verse */}
        <div className="mt-5 inline-flex animate-subtle-pulse items-center gap-2 rounded-full bg-brand-energy px-5 py-2 text-sm font-bold text-[#071114]">
          Cupos limitados · Los primeros serán miembros Founder
        </div>

        {/* Contador — números grandes, sin recuadros */}
        <div className="mt-6 flex items-start justify-center gap-7 md:gap-9">
          {[
            { n: cd.d, l: 'días' },
            { n: cd.h, l: 'horas' },
            { n: cd.m, l: 'min' },
            { n: cd.s, l: 'seg' },
          ].map((u) => (
            <div key={u.l} className="text-center">
              <div className="font-display text-4xl font-bold tabular-nums md:text-5xl">
                {String(u.n).padStart(2, '0')}
              </div>
              <div className="mt-1.5 text-[10px] uppercase tracking-[0.22em] text-white/35">{u.l}</div>
            </div>
          ))}
        </div>
        <p className="mt-4 text-xs text-white/35">
          Lunes 24 de agosto · 9:00 a. m. (GMT-5, hora Colombia/Perú)
        </p>

        {/* Waitlist */}
        {done ? (
          <p className="mt-7 text-sm font-medium text-brand-energy">
            Listo — te avisamos apenas abramos, con tu cupo Founder guardado.
          </p>
        ) : (
          <form onSubmit={submit} className="mx-auto mt-6 flex max-w-md flex-col gap-2.5 sm:flex-row">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Tu correo"
              className="flex-1 rounded-full border border-white/15 bg-white/[0.06] px-5 py-3.5 text-white placeholder:text-white/35 focus:border-brand-accent focus:outline-none"
            />
            <button
              type="submit"
              disabled={join.isPending}
              className="rounded-full bg-brand-energy px-7 py-3.5 font-semibold text-[#071114] transition-opacity hover:opacity-90 disabled:opacity-60"
            >
              {join.isPending ? 'Enviando…' : 'Quiero mi cupo'}
            </button>
          </form>
        )}
        {join.isError && !done && (
          <p className="mt-2 text-sm text-red-400">No pudimos guardar tu email. Intenta de nuevo.</p>
        )}
      </section>

      {/* Reel de contenido real, intercalado — imágenes grandes (mobile-first) */}
      {reel.length > 0 && (
        <section className="mt-6 overflow-hidden pb-12">
          {/* 90s (antes 60s): la animación recorre -50% del ancho total, así que
              con más tarjetas (16 → 24) la misma duración se veía 1.5x más
              rápida. 90s mantiene la velocidad en píxeles de siempre. */}
          <div className="flex w-max items-center gap-4" style={{ animation: 'gurumba-marquee 90s linear infinite' }}>
            {reel.map((it, i) => {
              const esGuru = it.kind === 'guru';
              return (
              <div
                key={`${it.key}-${i}`}
                className={`shrink-0 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] ${
                  esGuru ? 'w-44' : 'w-60'
                }`}
              >
                <div
                  className={`relative w-full overflow-hidden bg-[#0e1d23] ${
                    esGuru ? 'aspect-[3/4]' : 'aspect-[16/9]'
                  }`}
                >
                  {/* Todas las tarjetas miden lo mismo de alto; lo que cambia es
                      el ancho. El guru va angosto (vertical) y la portada de
                      curso o evento va ancha (horizontal). Así el carrusel
                      alterna formatos y se distingue de un vistazo qué es una
                      persona y qué es contenido.

                      Cada tarjeta mide lo que mide: la proporción de su imagen
                      define su alto, y la fila va centrada en vez de estirarlas.
                      Con un alto común, la portada horizontal se pegaba arriba y
                      dejaba un hueco negro debajo.

                      Guru: 3/4 y object-cover — la tarjeta tiene proporción de
                      retrato, así que la foto la llena sin franjas y sin achicar
                      el rostro. `object-top` porque en una foto de cuerpo entero
                      la cara está arriba.

                      Curso y evento: object-contain. Sus portadas traen texto y
                      recortarlas les comería el título. Nada de fondo borroso
                      para rellenar: iOS Safari rompe el compositor con muchos
                      blurs y la página sale en blanco. */}
                  <img
                    src={it.img}
                    alt={it.label}
                    className={`relative h-full w-full ${
                      it.kind === 'guru' ? 'object-cover object-top' : 'object-contain'
                    }`}
                    loading="lazy"
                    decoding="async"
                  />
                  <span className={`absolute left-2 top-2 z-10 rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ${kindColor[it.kind]}`}>
                    {kindTag[it.kind]}
                  </span>
                </div>
                <div className="px-3 py-3">
                  <p className="truncate text-sm font-semibold leading-tight">{it.label}</p>
                  {it.sub && <p className="mt-0.5 truncate text-xs text-white/50">{it.sub}</p>}
                </div>
              </div>
              );
            })}
          </div>
        </section>
      )}

      <footer className="border-t border-white/10 py-8 text-center text-xs text-white/40">
        © {new Date().getFullYear()} Gurumba · Muy pronto en toda Latinoamérica
      </footer>
    </div>
  );
}
